/* eslint-disable no-param-reassign */
/**
 * @fileoverview Hook de Actions Layer para gestión completa de envío y actualización de mensajes en Inbox (Mercately).
 * Optimizado para internacionalización, generación de identificadores únicos y manejo de errores.
 * Cumple con la checklist Mercately: typedefs, autoría, retorno documentado y métodos de orden.
 * @module useCompleteMessageActions
 * @category Hook - Actions Layer
 * @since 2.0.0
 * @version 3.2.0
 */
import { useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import useMessageActions from './useMessageActions';
import { LOAD_DATA_FAILURE } from '../../../../actions/inbox/conversationActions';
import { updateOrInsertConversationOptimized } from '../../../../actions/inbox/conversationOptimizedActions';
import {
  dispatchMessageAction,
  dispatchFastAnswersAction
} from '../../../../actions/inbox/sendMessageActions';
import {
  agentSignatureName,
  shouldSignAgentMessage,
  signAgentText
} from '../../../utils/agentSignatureUtils';

/**
 * Hook de Actions Layer para gestión completa de mensajes en Inbox.
 */
const useCompleteMessageActions = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation('InboxMC');
  const { addMessage, updateMessage } = useMessageActions();
  const currentRetailerUser = useSelector((state) => state.mainReducer?.currentRetailerUser);
  const attachAgentName = useSelector((state) => Boolean(state.retailerUsersReducer?.retailer_info?.attach_agent_name));

  /**
   * Quien está enviando, para la burbuja optimista.
   *
   * El backend lo devuelve como `sender_full_name`, pero solo cuando responde:
   * hasta entonces la burbuja ya está en pantalla y sin esto aparece sin firma y
   * se le añade de golpe al llegar la notificación en tiempo real. Quien pulsa
   * enviar es siempre el usuario de la sesión, así que aquí se sabe desde el
   * primer render. Vacío si el perfil aún no cargó — el componente solo lo
   * pinta cuando tiene valor, y la respuesta del backend lo rellenará.
   */
  const senderFullName = useMemo(() => {
    const parts = [currentRetailerUser?.first_name, currentRetailerUser?.last_name];
    return parts.filter(Boolean).join(' ').trim() || currentRetailerUser?.email || '';
  }, [currentRetailerUser]);

  /**
   * Firma la burbuja optimista como lo hará el backend con la regla "Incluir el nombre del agente".
   * Sin esto la burbuja sale sin nombre y el texto cambia cuando llega el mensaje guardado por socket.
   * Solo cambia lo que se pinta: lo que se envía a la API sigue sin firma, porque la pone el backend.
   */
  const signatureName = useMemo(() => agentSignatureName(currentRetailerUser), [currentRetailerUser]);
  const signLocalText = useCallback((text, { platform, type, note, template, isComment }) => {
    if (!shouldSignAgentMessage({ enabled: attachAgentName, platform, type, note, template, isComment })) {
      return text;
    }
    return signAgentText(text, platform, signatureName);
  }, [attachAgentName, signatureName]);

  /**
   * Quita la etiqueta "Tu turno" del chat en la lista en cuanto el agente responde, sin esperar al socket.
   * El backend la apaga con cualquier mensaje saliente salvo las notas (no mueven la última interacción), así
   * que esto solo adelanta lo que confirmará la notificación. Solo toca chats que ya están en la lista con la
   * etiqueta: `UPDATE_OR_ADD_CONVERSATION` insertaría un chat vacío si no existe.
   */
  const clearYourTurnLabel = useCallback((localMessage) => {
    if (localMessage?.direction !== 'outbound' || localMessage.note) return;

    dispatch((innerDispatch, getState) => {
      const {
        data, conversationIndexMap, externalConversation, selectedConversationId
      } = getState().conversationsReducer;
      const id = localMessage.chat_id || selectedConversationId;
      if (id == null) return;

      const key = String(id);
      const index = conversationIndexMap?.[key];
      const conversation = index !== undefined ? data[index] : null;
      const external = String(externalConversation?.id) === key ? externalConversation : null;
      if (!(conversation || external)?.attributes?.your_turn) return;

      innerDispatch(updateOrInsertConversationOptimized({ id: key, changes: { your_turn: false } }));
    });
  }, [dispatch]);

  /** Agrega la burbuja optimista de un envío y adelanta en la lista lo que cambia al responder. */
  const addOutboundMessage = useCallback(async (msg) => {
    const result = await addMessage(msg);
    if (result.success) clearYourTurnLabel(msg);
    return result;
  }, [addMessage, clearYourTurnLabel]);

  /**
   * Convierte un objeto plano a FormData para envío de archivos.
   */
  const toFormData = useCallback((obj) => {
    const formData = new FormData();
    Object.entries(obj).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        value.forEach((v) => formData.append(`${key}[]`, v));
      } else {
        formData.append(key, value);
      }
    });
    return formData;
  }, []);

  /**
   * Agrega mensaje local y maneja error si ocurre.
   */
  const addLocalMessage = useCallback(async (msg) => {
    const result = await addOutboundMessage(msg);
    if (!result.success) {
      dispatch({
        type: LOAD_DATA_FAILURE,
        error: result.error || t('messages.error')
      });
    }
    return result;
  }, [addOutboundMessage, dispatch, t]);

  /**
   * Construye el payload para agregar un mensaje local.
   */
  const buildLocalMessagePayload = useCallback((message, isComment) => {
    const isLocation = message.type === 'location';
    const contentType = isLocation ? 'location' : (message.content_type || message.type || 'text');
    return {
      ...message,
      content_text: signLocalText(message.message, {
        platform: message.platform,
        type: message.type,
        note: message.note,
        template: message.template,
        isComment
      }),
      content_type: contentType,
      isComment,
      direction: 'outbound',
      sender_full_name: senderFullName,
      created_time: new Date().toISOString(),
      identifier: message.identifier || uuidv4(),
      ...(isLocation ? {
        content_type: 'location',
        content_location_latitude: message.latitude,
        content_location_longitude: message.longitude
      } : {})
    };
  }, [senderFullName, signLocalText]);

  /**
   * Agrega mensaje local y lo envía a la API (sin archivos).
   */
  const sendAndAddMessage = useCallback(async ({ isComment, ...message }) => {
    const localMessage = buildLocalMessagePayload(message, isComment);
    const addResult = await addLocalMessage(localMessage);
    if (!addResult.success && !message.template) return addResult;
    if (isComment) {
      message.comment_identifier = localMessage.identifier;
    } else {
      message.message_identifier = localMessage.identifier;
    }
    message.action_type = 'create';
    dispatch(dispatchMessageAction(message));
    return { success: true, message: t('messages.localAdded') };
  }, [dispatch, addLocalMessage, t, buildLocalMessagePayload]);

  /**
   * Envía mensaje a la API (sin agregar local).
   */
  const sendMessageOnly = useCallback(async (messageData) => {
    messageData.action_type = 'create';
    await dispatch(dispatchMessageAction(messageData));
    return { success: true, message: t('messages.apiSent') };
  }, [dispatch, t]);

  /**
   * Genera identificadores únicos para mensajes con archivos.
   */
  const generateMessageIdentifiers = useCallback((count) => {
    if (count > 1) {
      return Array.from({ length: count }, () => uuidv4());
    }
    return [uuidv4()];
  }, []);

  /**
   * Construye el payload para agregar un mensaje local con archivo.
   */
  const buildLocalFileMessagePayload = useCallback((message, customer_id, platform, file, message_identifier) => ({
    ...message,
    customer_id,
    platform,
    filename: file?.name || message.file_name,
    // Kept for optimistic blob: playback — Safari needs the real Blob MIME, not a guessed mpeg type.
    content_media_mime_type: file?.type || message.content_media_mime_type || undefined,
    content_media_caption: signLocalText(message.caption, {
      platform,
      type: message.type,
      note: message.note,
      template: message.template,
      isComment: message.isComment
    }) || undefined,
    content_type: message.type === 'file' ? 'document' : message.type,
    direction: 'outbound',
    sender_full_name: senderFullName,
    content_media_url: file ? URL.createObjectURL(file) : message.file_url || message.url,
    created_time: new Date().toISOString(),
    identifier: message_identifier || uuidv4()
  }), [senderFullName, signLocalText]);

  /**
   * Agrega mensaje local y lo envía a la API con archivos adjuntos (uno o varios).
   */
  /**
   * Divide un mensaje en dos burbujas (Texto y Archivo) según la plataforma y tipo.
   */
  const splitMessagePayloads = useCallback(async (messageData) => {
    const isAudio = messageData.type === 'audio';
    const textId = uuidv4();
    const fileId = uuidv4();

    const textPayload = buildLocalMessagePayload({
      ...messageData,
      type: 'text',
      message: messageData.caption || messageData.message || '',
      url: undefined,
      file_url: undefined,
      identifier: textId
    }, messageData.isComment);

    const filePayload = buildLocalFileMessagePayload({
      ...messageData,
      caption: undefined,
      reply_to: undefined
    }, messageData.customer_id, messageData.platform, null, fileId);

    // Audio: Texto primero; Otros (Meta): Archivo primero
    const sequence = isAudio ? [textPayload, filePayload] : [filePayload, textPayload];

    for (const payload of sequence) {
      // eslint-disable-next-line no-await-in-loop
      await addOutboundMessage(payload);
    }

    return { textMessage: textPayload, fileMessage: filePayload, identifiers: [sequence[0].identifier, sequence[1].identifier] };
  }, [addOutboundMessage, buildLocalFileMessagePayload, buildLocalMessagePayload]);

  /**
   * Agrega mensaje local y lo envía a la API con archivos adjuntos (uno o varios).
   */
  const sendAndAddMessageWithFiles = useCallback(async (messageData) => {
    const { file_data, ...message } = messageData;
    const isMultiple = Array.isArray(file_data) && file_data?.length > 1;
    const files = Array.isArray(file_data) ? file_data : [file_data];
    const isMeta = ['messenger', 'instagram', 'facebook'].includes(messageData.platform);
    const isMedia = ['image', 'video', 'file', 'document'].includes(message.type);
    const shouldSplit = !isMultiple && isMeta && isMedia && message.caption;

    if (shouldSplit) {
      const { identifiers } = await splitMessagePayloads(messageData);
      messageData.message_identifier = identifiers[0];
      messageData.message_identifiers = [identifiers[1]];
    } else {
      const message_identifiers = generateMessageIdentifiers(files.length);
      if (isMultiple) {
        messageData.message_identifiers = message_identifiers;
      } else {
        messageData.message_identifier = message_identifiers[0];
      }

      for (let i = 0; i < files.length; i += 1) {
        const fileMsg = buildLocalFileMessagePayload(
          message,
          messageData.customer_id,
          messageData.platform,
          files[i],
          message_identifiers[i]
        );
        // eslint-disable-next-line no-await-in-loop
        await addLocalMessage(fileMsg);
      }
    }

    if (isMultiple) {
      messageData.action_type = 'send_bulk_files';
    } else {
      messageData.action_type = 'send_file';
      if (file_data) {
        messageData.file_data = Array.isArray(file_data) ? file_data[0] : file_data;
      }
    }

    const fd = toFormData(messageData);
    dispatch(dispatchMessageAction(fd));
    return { success: true, message: t('messages.localFileAdded') };
  }, [dispatch, addLocalMessage, toFormData, generateMessageIdentifiers, t, buildLocalFileMessagePayload, splitMessagePayloads]);

  /**
   * Envía mensaje con archivos a la API (sin agregar local).
   */
  const sendMessageWithFilesOnly = useCallback(async (messageData) => {
    const isMultiple = Array.isArray(messageData?.file_data) && messageData?.file_data?.length > 1;
    if (isMultiple) {
      messageData.action_type = 'send_bulk_files';
    } else {
      messageData.action_type = 'send_file';
      const file = Array.isArray(messageData.file_data) ? messageData.file_data[0] : messageData.file_data;
      messageData.file_data = file;
    }
    const fd = toFormData(messageData);
    await dispatch(dispatchMessageAction(fd));
    return { success: true, message: t('messages.apiFileSent') };
  }, [dispatch, toFormData, t]);

  /**
   * Genera el payload para un mensaje de orden.
   */
  const generateOrderMessagePayload = useCallback(({ customer_id, orderData, platform }) => {
    const id = uuidv4();
    return {
      note: true,
      order_note: true,
      message: 'Orden',
      customer_id,
      order_summary: orderData,
      type: 'text',
      action_type: 'create',
      platform,
      identifier: id,
      message_identifier: id
    };
  }, []);

  /**
   * Agrega mensaje de orden local y lo envía a la API.
   */
  const sendAndAddMessageOrder = useCallback(async (orderMessage) => {
    const messageData = generateOrderMessagePayload(orderMessage);
    const addResult = await addLocalMessage({
      ...messageData,
      content_type: 'text',
      direction: 'outbound',
      sender_full_name: senderFullName,
      status: 'enqueued',
      created_time: new Date()
    });
    if (!addResult.success) return addResult;
    dispatch(dispatchMessageAction(messageData));
    return { success: true, message: t('messages.localOrderAdded') };
  }, [dispatch, addLocalMessage, generateOrderMessagePayload, senderFullName, t]);

  /**
   * Envía mensaje de orden a la API (sin agregar local).
   */
  const sendMessageOrderOnly = useCallback(async (orderMessage) => {
    const messageData = generateOrderMessagePayload(orderMessage);
    dispatch(dispatchMessageAction(messageData));
    return { success: true, message: t('messages.apiOrderSent') };
  }, [dispatch, generateOrderMessagePayload, t]);

  /**
   * Envía múltiples respuestas rápidas a la API.
   */
  const sendMultipleFastAnswers = useCallback(async (messageData) => {
    const fd = messageData.type === "text" ? messageData : toFormData(messageData);
    dispatch(dispatchFastAnswersAction(fd));
    return { success: true, message: t('messages.apiFastAnswersSent') };
  }, [dispatch, t, toFormData]);

  /**
   * Construye el payload para un mensaje de carrusel basado en plantilla API.
   */
  const buildLocalMessageCarouselPayload = useCallback((messageData, selectedTemplate) => {
    const { cards_files, template_params: templateParams } = messageData;
    const files = Array.isArray(cards_files) ? cards_files : [cards_files];

    // Helper para reemplazar placeholders {{1}}, {{2}}, ... por templateParams en orden global
    let paramIdx = 0;
    const replacePlaceholdersOrdered = (str) => {
      if (!str || !templateParams) return str;
      return str.replace(/{{\d+}}/g, () => {
        const value = templateParams[paramIdx];
        paramIdx += 1;
        return value !== undefined ? value : '';
      });
    };

    // Reemplazo secuencial en todos los campos usando el mismo índice global
    const contentText = replacePlaceholdersOrdered(selectedTemplate?.text ?? '');
    const carouselCards = selectedTemplate?.cards?.map((card, index) => {
      const body = replacePlaceholdersOrdered(card.body);
      const buttons = card.buttons.map((btn) => ({
        text: btn.text,
        url: replacePlaceholdersOrdered(btn.url)
      }));
      return {
        body,
        link: URL.createObjectURL(files[index]),
        buttons
      };
    }) || [];

    return {
      ...messageData,
      identifier: messageData.message_identifier,
      card_header_type: selectedTemplate?.cards?.[0]?.headerType?.toLowerCase() || 'image',
      content_text: contentText,
      content_type: selectedTemplate?.templateType,
      direction: 'outbound',
      sender_full_name: senderFullName,
      status: 'enqueued',
      carousel_cards: carouselCards,
      created_time: new Date()
    };
  }, [senderFullName]);

  /**
   * Agrega mensaje de carrusel local y lo envía a la API (manejo especial para múltiples archivos en carousel).
   */
  const sendAndAddMessageCarousel = useCallback(async (messageData, selectedTemplate) => {
    const localMessage = buildLocalMessageCarouselPayload(messageData, selectedTemplate);
    await addLocalMessage(localMessage);
    messageData.action_type = 'send_file';
    messageData.caption = localMessage.content_text;
    const fd = toFormData(messageData);
    dispatch(dispatchMessageAction(fd));
    return { success: true, message: t('messages.localFileAdded') };
  }, [dispatch, addLocalMessage, toFormData, t, buildLocalMessageCarouselPayload]);

  return useMemo(() => ({
    addMessage: addOutboundMessage,
    updateMessage,
    sendMessageOnly,
    sendAndAddMessage,
    sendAndAddMessageWithFiles,
    sendMessageWithFilesOnly,
    buildLocalMessagePayload,
    buildLocalFileMessagePayload,
    sendAndAddMessageOrder,
    sendMessageOrderOnly,
    sendMultipleFastAnswers,
    sendAndAddMessageCarousel,
    splitMessagePayloads
  }), [
    addOutboundMessage,
    updateMessage,
    sendMessageOnly,
    sendAndAddMessage,
    sendAndAddMessageWithFiles,
    sendMessageWithFilesOnly,
    buildLocalMessagePayload,
    buildLocalFileMessagePayload,
    sendAndAddMessageOrder,
    sendMessageOrderOnly,
    sendMultipleFastAnswers,
    sendAndAddMessageCarousel,
    splitMessagePayloads
  ]);
};

export default useCompleteMessageActions;
